import { motion } from "framer-motion";
import { ArrowRight, GraduationCap, LayoutDashboard, PenLine } from "lucide-react";
import pageOne from "@/assets/pdf-pages/page-1.png";
import pageThree from "@/assets/pdf-pages/page-3.png";
import OrderNowLink from "@/components/OrderNowLink";
import { LAUNCH_COUPON_CODE, LAUNCH_COUPON_DISCOUNT } from "@/lib/order";

export default function HeroSection() {
  return (
    <section className="relative isolate overflow-hidden bg-[#F8F2E8] px-4 py-14 sm:px-5 md:px-10 md:py-28">
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_20%_15%,rgba(213,173,118,0.26),transparent_28%),radial-gradient(circle_at_78%_22%,rgba(143,240,210,0.32),transparent_24%),linear-gradient(135deg,#FBF7EF_0%,#F8F2E8_48%,#F2FFF9_100%)]" />
      <div className="mx-auto grid max-w-7xl items-center gap-10 lg:grid-cols-[1.05fr_0.95fr]">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.75, delay: 0.15 }}
        >
          <h1 className="max-w-4xl font-heading text-4xl uppercase leading-[0.98] text-[#073C2F] sm:text-5xl md:text-7xl">
            <span className="block">Note Console</span>
            <span className="mt-4 block w-fit max-w-full rounded-xl border border-[#D5AD76] bg-white/70 px-4 py-3 font-body text-xl font-bold normal-case leading-tight tracking-normal text-[#27463B] shadow-sm sm:text-2xl md:text-3xl">
              for the MBA life you are building.
            </span>
          </h1>
          <div className="mt-6 max-w-2xl font-body text-[#27463B]">
            <p className="text-xl font-bold leading-8 text-[#073C2F] md:text-3xl">
              Built for the chaos of MBA life.
            </p>
            <p className="mt-3 text-base leading-7 md:text-lg md:leading-8">
              One planner for classes, committees, competitions, internships, placements, and everything in between.
            </p>
            <p className="mt-4 inline-flex max-w-full rounded-lg border border-[#D5AD76] bg-retro-yellow px-4 py-2 text-sm font-black uppercase tracking-[0.04em] text-[#073C2F] sm:text-base sm:tracking-[0.08em]">
              Physical Planner + Digital Companion
            </p>
            <p className="mt-4 text-base font-semibold leading-7 text-[#073C2F]/85 md:text-lg md:leading-8">
              Always with you. Always up to date.
            </p>
            <p className="mt-4 inline-flex max-w-full rounded-lg border border-[#073C2F] bg-white px-4 py-2 text-sm font-black uppercase text-[#073C2F] sm:text-base">
              Launch coupon {LAUNCH_COUPON_CODE}: {LAUNCH_COUPON_DISCOUNT} off
            </p>
          </div>
          <div className="mt-6 grid max-w-2xl gap-3 sm:grid-cols-3">
            {[
              { icon: PenLine, label: "Physical planner pages" },
              { icon: LayoutDashboard, label: "Digital companion" },
              { icon: GraduationCap, label: "MBA-focused structure" },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-2 rounded-lg border border-[#D5AD76] bg-white/60 px-3 py-3 text-[#073C2F] shadow-sm">
                <item.icon size={18} className="shrink-0" />
                <span className="font-body text-sm font-bold leading-tight">{item.label}</span>
              </div>
            ))}
          </div>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:gap-4">
            <a href="#pdf-showcase" className="retro-btn inline-flex items-center justify-center gap-2 bg-[#073C2F] px-5 py-3 text-white">
              Preview Pages <ArrowRight size={18} />
            </a>
            <OrderNowLink source="hero" showIcon className="retro-btn inline-flex items-center justify-center gap-2 bg-retro-yellow px-5 py-3 text-retro-ink" />
          </div>
        </motion.div>

        <motion.div
          className="relative mx-auto w-full max-w-[360px] sm:max-w-[460px] lg:max-w-[560px]"
          initial={{ opacity: 0, scale: 0.94, rotate: 2 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ duration: 0.8, delay: 0.35, type: "spring", stiffness: 90 }}
        >
          <div className="absolute -left-3 top-8 z-0 h-[76%] w-[72%] rotate-[-7deg] overflow-hidden rounded-2xl border border-[#D5AD76] bg-white shadow-2xl sm:-left-5 sm:top-10 sm:rotate-[-8deg]">
            <img src={pageThree} alt="" className="h-full w-full object-cover opacity-70" loading="lazy" />
          </div>
          <div className="relative z-10 overflow-hidden rounded-2xl border-[3px] border-[#073C2F] bg-white p-3 shadow-[0_28px_90px_rgba(7,60,47,0.24)]">
            <img src={pageOne} alt="Planner page preview" className="aspect-[4/5] w-full rounded-xl object-cover" loading="eager" />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
